import { useEffect, useState } from "react";
import api from "@/lib/api";
import { currency } from "@/lib/portalConstants";
import { printReceipt, printInvoice } from "@/lib/printTemplates";
import { Loader2, ReceiptText, Printer, FileText, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/portal/receipts").then((r) => setReceipts(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = receipts.filter((r) => {
    const s = q.toLowerCase();
    return (
      !s ||
      (r.receipt_number || "").toLowerCase().includes(s) ||
      (r.invoice_number || "").toLowerCase().includes(s) ||
      (r.client?.name || "").toLowerCase().includes(s) ||
      (r.paid_by || "").toLowerCase().includes(s)
    );
  });

  const totalCollected = receipts.reduce((sum, r) => sum + (r.amount_paid != null ? r.amount_paid : r.total || 0), 0);

  return (
    <div className="space-y-8" data-testid="receipts-page">
      <div>
        <h1 className="font-serif text-4xl text-charcoal">Receipts</h1>
        <p className="text-charcoal-soft mt-1">Your payment records — one touch from receipt to invoice.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:max-w-md">
            <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-5">
              <p className="font-serif text-3xl text-sage-700">{receipts.length}</p>
              <p className="text-sm text-charcoal-soft">Receipts on file</p>
            </div>
            <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-5">
              <p className="font-serif text-3xl text-sage-700">{currency(totalCollected)}</p>
              <p className="text-sm text-charcoal-soft">Total collected</p>
            </div>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal/40" />
            <Input data-testid="receipt-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search receipt, invoice, client…" className="pl-9 bg-white" />
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
              <ReceiptText className="h-10 w-10 text-sage/50 mx-auto mb-4" />
              <p className="text-charcoal-soft">{receipts.length === 0 ? "No receipts yet. When you record a payment on an invoice, its receipt appears here." : "No receipts match your search."}</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow overflow-hidden">
              <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 text-xs uppercase tracking-wide text-charcoal-soft border-b border-charcoal/5">
                <span className="col-span-2">Receipt</span>
                <span className="col-span-2">Invoice</span>
                <span className="col-span-3">Client / Paid by</span>
                <span className="col-span-2">Date · Method</span>
                <span className="col-span-1 text-right">Amount</span>
                <span className="col-span-2 text-right">Actions</span>
              </div>
              <div className="divide-y divide-charcoal/5">
                {filtered.map((r) => (
                  <div key={r.id} data-testid={`receipt-row-${r.id}`} className="grid grid-cols-2 sm:grid-cols-12 gap-3 px-5 py-4 items-center">
                    <span className="sm:col-span-2 font-medium text-emerald-700">{r.receipt_number}</span>
                    <span className="sm:col-span-2 text-charcoal">{r.invoice_number}</span>
                    <span className="sm:col-span-3 min-w-0">
                      <span className="block text-charcoal truncate">{r.client?.name || "—"}</span>
                      <span className="block text-xs text-charcoal-soft truncate">{r.paid_by}</span>
                    </span>
                    <span className="sm:col-span-2 text-sm text-charcoal-soft">{r.paid_at} · {r.payment_method}</span>
                    <span className="sm:col-span-1 sm:text-right font-medium text-charcoal">{currency(r.amount_paid != null ? r.amount_paid : r.total)}</span>
                    <span className="sm:col-span-2 flex sm:justify-end gap-1">
                      <button onClick={() => printReceipt(r)} data-testid={`print-receipt-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs hover:bg-emerald-700 transition-colors"><Printer className="h-3.5 w-3.5" /> Receipt</button>
                      <button onClick={() => printInvoice(r)} data-testid={`print-inv-${r.id}`} className="inline-flex items-center gap-1 rounded-full border border-charcoal/15 px-3 py-1.5 text-xs text-charcoal-soft hover:bg-sand transition-colors"><FileText className="h-3.5 w-3.5" /> Invoice</button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
